import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { login, signup, clearError } from '../../store/authSlice'
import GrowthHouseLogo from '../base/GrowthHouseLogo'

function parseHashError(): string | null {
  const hash = window.location.hash.replace(/^#/, '')
  if (!hash) return null
  const params = new URLSearchParams(hash)
  const code = params.get('error_code') ?? params.get('error')
  if (!code) return null
  if (code === 'otp_expired') {
    return 'That email link has expired. Please sign in, or request a new one.'
  }
  const description = params.get('error_description')?.replace(/\+/g, ' ')
  return description || 'That email link could not be used. Please try again.'
}

export default function LoginScreen() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { isLoading, error } = useAppSelector((s) => s.auth)

  const [tab, setTab] = useState<'signin' | 'signup'>('signin')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [registrationCode, setRegistrationCode] = useState('')
  const [localError, setLocalError] = useState('')

  useEffect(() => {
    const hashErr = parseHashError()
    if (hashErr) {
      setLocalError(hashErr)
      window.history.replaceState(null, '', window.location.pathname + window.location.search)
    }
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    dispatch(clearError())
    setLocalError('')

    if (!email || !password) {
      setLocalError('Please enter email and password.')
      return
    }

    if (tab === 'signup') {
      if (password.length < 6) {
        setLocalError('Password must be at least 6 characters.')
        return
      }
      if (password !== confirmPassword) {
        setLocalError('Passwords do not match.')
        return
      }
      if (!registrationCode.trim()) {
        setLocalError('Please enter your registration code.')
        return
      }
      const result = await dispatch(signup({ email, password, registrationCode: registrationCode.trim() }))
      if (signup.fulfilled.match(result)) {
        navigate('/')
      }
    } else {
      const result = await dispatch(login({ email, password }))
      if (login.fulfilled.match(result)) {
        navigate('/')
      }
    }
  }

  return (
    <div className="onboard-wrap" data-testid="login-screen">
      <div className="onboard-box">
        <div className="gate-logo" data-testid="login-logo">
          <GrowthHouseLogo size={56} />
        </div>
        <h1 className="gate-title">The Teamship Tools</h1>
        <div className="gate-tabs" data-testid="auth-tabs">
          <button
            className={`gate-tab ${tab === 'signin' ? 'active' : ''}`}
            onClick={() => { setTab('signin'); dispatch(clearError()); setLocalError('') }}
            data-testid="tab-signin"
          >
            Sign In
          </button>
          <button
            className={`gate-tab ${tab === 'signup' ? 'active' : ''}`}
            onClick={() => { setTab('signup'); dispatch(clearError()); setLocalError('') }}
            data-testid="tab-signup"
          >
            Sign Up
          </button>
        </div>

        <form onSubmit={handleSubmit} data-testid="auth-form">
          <input
            type="email"
            className="gate-input"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            data-testid="login-email-input"
          />
          <input
            type="password"
            className="gate-input"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete={tab === 'signin' ? 'current-password' : 'new-password'}
            data-testid="login-password-input"
          />
          {tab === 'signup' && (
            <>
              <input
                type="password"
                className="gate-input"
                placeholder="Confirm password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
                data-testid="login-confirm-password-input"
              />
              <input
                type="text"
                className="gate-input"
                placeholder="Registration code"
                value={registrationCode}
                onChange={(e) => setRegistrationCode(e.target.value)}
                autoComplete="off"
                data-testid="login-registration-code-input"
              />
            </>
          )}

          {(error || localError) && (
            <div className="gate-err" data-testid="auth-error">{localError || error}</div>
          )}

          <button
            type="submit"
            className="btn btn-primary gate-btn"
            disabled={isLoading}
            data-testid="auth-submit-btn"
          >
            {isLoading ? 'Please wait...' : tab === 'signin' ? 'Sign In' : 'Sign Up'}
          </button>
        </form>

        {tab === 'signin' && (
          <div className="gate-links">
            <button
              className="link-btn"
              onClick={() => navigate('/reset-password')}
              data-testid="forgot-password-link"
            >
              Forgot password?
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
